import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format, getISODay, isAfter, isBefore, parseISO } from 'date-fns';
import { supabase } from '../lib/supabase';
import { errorMessage, formatDuration, formatTime } from '../lib/format';
import { dateKey, generateNextBlock, generateSessionStarts } from '../lib/schedule';
import type { ClassRow, ClassType, Holiday, Instructor, SessionStatus } from '../lib/types';
import { PageHead } from '../components/AppLayout';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';

// Class joined with everything the list needs in one query.
interface ClassListRow extends ClassRow {
  class_types: { name: string; color: string } | null;
  instructors: { name: string } | null;
  enrollments: { count: number }[];
  class_sessions: { starts_at: string; status: SessionStatus }[];
}

const LIST_SELECT =
  '*, class_types(name, color), instructors(name), enrollments(count), class_sessions(starts_at, status)';

function classLabel(c: ClassListRow): string {
  return c.title || c.class_types?.name || 'Class';
}

// Summarise a class's sessions: how many are done, what's next.
function sessionFacts(c: ClassListRow) {
  const now = new Date();
  const scheduled = c.class_sessions
    .filter((s) => s.status === 'scheduled')
    .sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  const done = scheduled.filter((s) => isBefore(parseISO(s.starts_at), now)).length;
  const next = scheduled.find((s) => isAfter(parseISO(s.starts_at), now));
  return { total: scheduled.length, done, next: next ? parseISO(next.starts_at) : null };
}

// Recurring classes never run out: once a block's last session has
// passed, generate the next block of `sessions_count` weekly sessions
// continuing right where it left off — same class, same roster. Returns
// whether anything was extended.
async function extendRecurringClasses(list: ClassListRow[]): Promise<boolean> {
  const now = new Date();
  const due = list.filter((c) => {
    if (!c.recurring || c.status !== 'active' || c.class_sessions.length === 0) return false;
    const hasUpcoming = c.class_sessions.some(
      (s) => s.status === 'scheduled' && !isBefore(parseISO(s.starts_at), now),
    );
    return !hasUpcoming;
  });
  if (due.length === 0) return false;

  const { data: holidaysData } = await supabase.from('holidays').select('holiday_date');
  const holidaySet = new Set((holidaysData ?? []).map((h) => h.holiday_date as string));

  for (const c of due) {
    const sorted = [...c.class_sessions].sort((a, b) => a.starts_at.localeCompare(b.starts_at));
    const lastStart = parseISO(sorted[sorted.length - 1].starts_at);
    const plan = generateNextBlock(lastStart, c.sessions_count, holidaySet);
    if (plan.starts.length === 0) continue;
    const newSessions = plan.starts.map((s) => ({
      class_id: c.id,
      starts_at: s.toISOString(),
      ends_at: new Date(s.getTime() + c.duration_minutes * 60_000).toISOString(),
    }));
    await supabase.from('class_sessions').insert(newSessions);
  }
  return true;
}

export function ClassesPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ClassListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scheduling, setScheduling] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const reload = useCallback(async () => {
    const { data, error } = await supabase
      .from('classes')
      .select(LIST_SELECT)
      .order('start_at', { ascending: false });
    if (error) {
      setError(errorMessage(error));
      setLoading(false);
      return;
    }
    setError(null);
    let list = (data ?? []) as ClassListRow[];
    if (await extendRecurringClasses(list)) {
      const { data: refreshed } = await supabase
        .from('classes')
        .select(LIST_SELECT)
        .order('start_at', { ascending: false });
      list = (refreshed ?? []) as ClassListRow[];
    }
    setRows(list);
    setLoading(false);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const classTypeNames = useMemo(() => {
    const names = new Set<string>();
    for (const c of rows) if (c.class_types?.name) names.add(c.class_types.name);
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const sorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    let filtered = q
      ? rows.filter((c) =>
          [classLabel(c), c.class_types?.name, c.instructors?.name]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q)),
        )
      : rows;
    if (typeFilter) filtered = filtered.filter((c) => c.class_types?.name === typeFilter);
    // Monday → Friday (then Saturday, Sunday), earliest to latest time
    // within each day — reads like a weekly timetable. Use the type
    // filter above to narrow by class type instead of grouping by it.
    return [...filtered].sort((a, b) => {
      const da = parseISO(a.start_at);
      const db = parseISO(b.start_at);
      const dayCmp = getISODay(da) - getISODay(db);
      if (dayCmp !== 0) return dayCmp;
      return format(da, 'HH:mm').localeCompare(format(db, 'HH:mm'));
    });
  }, [rows, search, typeFilter]);

  return (
    <>
      <PageHead
        title="Classes"
        subtitle="Every class you've scheduled. Click one to manage its roster and dates."
        action={
          <button className="btn btn-primary" onClick={() => setScheduling(true)}>
            + Schedule class
          </button>
        }
      />

      <div className="card">
        <div className="row" style={{ marginBottom: '0.9rem', flexWrap: 'wrap' }}>
          <input
            placeholder="Search by class, type, or instructor…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 340 }}
          />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            aria-label="Filter by class type"
            style={{ maxWidth: 220 }}
          >
            <option value="">All class types</option>
            {classTypeNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>
        {error && <p className="error-text">{error}</p>}
        {loading ? (
          <div style={{ padding: '0.4rem 0' }}>
            <span className="skel" style={{ width: '70%' }} />
            <span className="skel" style={{ width: '55%' }} />
            <span className="skel" style={{ width: '62%' }} />
          </div>
        ) : sorted.length === 0 ? (
          <div className="empty">
            <span className="empty-emoji">🎓</span>
            {rows.length === 0 ? (
              <>
                No classes scheduled yet.
                <br />
                <button className="linkish" onClick={() => setScheduling(true)}>
                  Schedule your first class
                </button>{' '}
                — it'll appear on the calendar automatically.
              </>
            ) : (
              'No classes match that search or filter.'
            )}
          </div>
        ) : (
          <table className="table cards">
            <thead>
              <tr>
                <th>Day of week</th>
                <th>Class time</th>
                <th>Class type</th>
                <th>Instructor</th>
                <th>Start date</th>
                <th>Next session start</th>
                <th>Progress</th>
                <th>Enrolled</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => {
                const facts = sessionFacts(c);
                const enrolled = c.enrollments[0]?.count ?? 0;
                const startAt = parseISO(c.start_at);
                return (
                  <tr
                    key={c.id}
                    className="rowlink"
                    onClick={() => navigate(`/app/classes/${c.id}`)}
                  >
                    <td data-label="Day of week" style={{ fontWeight: 600 }}>
                      {format(startAt, 'EEEE')}
                    </td>
                    <td className="muted" data-label="Class time">
                      {format(startAt, 'h:mm a')}
                    </td>
                    <td data-label="Class type">
                      <span className="row" style={{ gap: '0.5rem' }}>
                        <span
                          className="dot"
                          style={{ background: c.class_types?.color ?? 'var(--brand)' }}
                        />
                        <span style={{ fontWeight: 600 }}>{classLabel(c)}</span>
                        {c.status === 'cancelled' && (
                          <span className="badge badge-danger">Cancelled</span>
                        )}
                      </span>
                    </td>
                    <td className="muted" data-label="Instructor">
                      {c.instructors?.name ?? '—'}
                    </td>
                    <td className="muted" data-label="Start date">
                      {format(startAt, 'MMM d, yyyy')}
                    </td>
                    <td className="muted" data-label="Next session start">
                      {c.status === 'cancelled'
                        ? '—'
                        : facts.next
                          ? `${format(facts.next, 'EEE MMM d')} · ${format(facts.next, 'h:mm a')}`
                          : facts.total > 0
                            ? 'Finished'
                            : '—'}
                    </td>
                    <td data-label="Progress">
                      <div className="row" style={{ gap: '0.5rem' }}>
                        <span className="progress">
                          <span
                            style={{
                              width: facts.total
                                ? `${Math.round((facts.done / facts.total) * 100)}%`
                                : 0,
                            }}
                          />
                        </span>
                        <span className="muted small">
                          {facts.done}/{facts.total}
                        </span>
                      </div>
                    </td>
                    <td data-label="Enrolled">
                      <span className="pill">
                        🐕 <b>{enrolled}</b>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {scheduling && (
        <ScheduleClassForm
          onClose={() => setScheduling(false)}
          onCreated={(id) => {
            setScheduling(false);
            navigate(`/app/classes/${id}`);
          }}
        />
      )}
    </>
  );
}

// ── Schedule class form ──────────────────────────────────────────────
// Creates the `classes` row plus its generated `class_sessions`, skipping
// holidays. Shows a live preview of the dates it will create.

export function ScheduleClassForm({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (classId: string) => void;
}) {
  const toast = useToast();
  const [types, setTypes] = useState<ClassType[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loaded, setLoaded] = useState(false);

  const [typeId, setTypeId] = useState('');
  const [instructorId, setInstructorId] = useState('');
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('18:00');
  const [duration, setDuration] = useState('60');
  const [weekly, setWeekly] = useState(true);
  const [weeks, setWeeks] = useState('8');
  const [recurring, setRecurring] = useState(false);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const [t, i, h] = await Promise.all([
        supabase.from('class_types').select('*').order('name'),
        supabase.from('instructors').select('*').eq('active', true).order('name'),
        supabase.from('holidays').select('*'),
      ]);
      setTypes((t.data ?? []) as ClassType[]);
      setInstructors((i.data ?? []) as Instructor[]);
      setHolidays((h.data ?? []) as Holiday[]);
      setLoaded(true);
    })();
  }, []);

  const holidaySet = useMemo(() => new Set(holidays.map((h) => h.holiday_date)), [holidays]);
  const holidayLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const h of holidays) m.set(h.holiday_date, h.label);
    return m;
  }, [holidays]);

  function pickType(id: string) {
    setTypeId(id);
    const t = types.find((x) => x.id === id);
    if (t?.default_duration_minutes) setDuration(String(t.default_duration_minutes));
  }

  const count = weekly ? Math.max(1, Math.min(52, Number(weeks) || 0)) : 1;
  const firstStart = useMemo(() => {
    if (!date || !time) return null;
    const d = new Date(`${date}T${time}`);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [date, time]);

  const plan = useMemo(() => {
    if (!firstStart) return null;
    return generateSessionStarts(firstStart, count, holidaySet);
  }, [firstStart, count, holidaySet]);

  // A one-off class directly on a no-class day is almost certainly a
  // mistake — block it with a clear message. (Weekly series simply skip
  // the holiday; the preview shows that.)
  const oneOffOnHoliday = !weekly && firstStart && holidaySet.has(dateKey(firstStart));

  const durationMin = Math.max(5, Number(duration) || 60);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!firstStart || !plan || plan.starts.length === 0 || oneOffOnHoliday) return;
    setBusy(true);
    setErr(null);

    const { data: created, error: classError } = await supabase
      .from('classes')
      .insert({
        class_type_id: typeId,
        instructor_id: instructorId,
        title: title.trim() || null,
        start_at: plan.starts[0].toISOString(),
        duration_minutes: durationMin,
        recurrence: weekly ? 'weekly' : 'none',
        sessions_count: count,
        recurring: weekly && recurring,
        notes: notes.trim() || null,
      })
      .select('id')
      .single();

    if (classError || !created) {
      setBusy(false);
      setErr(errorMessage(classError));
      return;
    }

    const sessions = plan.starts.map((s) => ({
      class_id: created.id,
      starts_at: s.toISOString(),
      ends_at: new Date(s.getTime() + durationMin * 60_000).toISOString(),
    }));
    const { error: sessError } = await supabase.from('class_sessions').insert(sessions);

    if (sessError) {
      // Don't leave a session-less class behind.
      await supabase.from('classes').delete().eq('id', created.id);
      setBusy(false);
      setErr(errorMessage(sessError));
      return;
    }

    toast.success(
      count === 1
        ? 'Class scheduled and added to the calendar.'
        : `Class scheduled — ${count} sessions added to the calendar.`,
    );
    onCreated(created.id);
  }

  const missingSetup = loaded && (types.length === 0 || instructors.length === 0);

  return (
    <Modal title="Schedule a class" onClose={onClose}>
      {missingSetup ? (
        <>
          <p className="muted" style={{ marginTop: 0 }}>
            Before scheduling, you need at least one class type and one instructor to pick from:
          </p>
          <ul className="muted" style={{ margin: '0 0 0.4rem', paddingLeft: '1.2rem' }}>
            {types.length === 0 && (
              <li>
                Add a <Link to="/app/class-types">class type</Link> (e.g. Puppy Workshop)
              </li>
            )}
            {instructors.length === 0 && (
              <li>
                Add an <Link to="/app/instructors">instructor</Link>
              </li>
            )}
          </ul>
          <div className="row" style={{ justifyContent: 'flex-end', marginTop: '0.8rem' }}>
            <button className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </>
      ) : (
        <form onSubmit={submit}>
          <div className="field-row">
            <div className="field">
              <label htmlFor="sc-type">Class type</label>
              <select
                id="sc-type"
                value={typeId}
                onChange={(e) => pickType(e.target.value)}
                required
                autoFocus
              >
                <option value="" disabled>
                  {loaded ? 'Choose a type…' : 'Loading…'}
                </option>
                {types.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="sc-instructor">Instructor</label>
              <select
                id="sc-instructor"
                value={instructorId}
                onChange={(e) => setInstructorId(e.target.value)}
                required
              >
                <option value="" disabled>
                  {loaded ? 'Choose an instructor…' : 'Loading…'}
                </option>
                {instructors.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="field">
            <label htmlFor="sc-title">Custom name (optional)</label>
            <input
              id="sc-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Defaults to the class type name"
            />
          </div>

          <div className="field-row">
            <div className="field">
              <label htmlFor="sc-date">First class date</label>
              <input
                id="sc-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="sc-time">Start time</label>
              <input
                id="sc-time"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="field-row">
            <div className="field">
              <label htmlFor="sc-repeat">Repeats</label>
              <select
                id="sc-repeat"
                value={weekly ? 'weekly' : 'none'}
                onChange={(e) => {
                  const isWeekly = e.target.value === 'weekly';
                  setWeekly(isWeekly);
                  if (!isWeekly) setRecurring(false);
                }}
              >
                <option value="weekly">Weekly series</option>
                <option value="none">One-time class</option>
              </select>
            </div>
            {weekly ? (
              <div className="field">
                <label htmlFor="sc-weeks">Number of sessions</label>
                <input
                  id="sc-weeks"
                  type="number"
                  min={1}
                  max={52}
                  value={weeks}
                  onChange={(e) => setWeeks(e.target.value)}
                  required
                />
              </div>
            ) : (
              <div className="field">
                <label htmlFor="sc-duration">Length (minutes)</label>
                <input
                  id="sc-duration"
                  type="number"
                  min={5}
                  step={5}
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  required
                />
              </div>
            )}
          </div>

          {weekly && (
            <div className="field">
              <label htmlFor="sc-duration2">Length of each session (minutes)</label>
              <input
                id="sc-duration2"
                type="number"
                min={5}
                step={5}
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                required
              />
            </div>
          )}

          {weekly && (
            <div className="field">
              <label className="row" style={{ gap: '0.5rem', alignItems: 'flex-start' }}>
                <input
                  type="checkbox"
                  checked={recurring}
                  onChange={(e) => setRecurring(e.target.checked)}
                  style={{ marginTop: '0.2rem' }}
                />
                <span style={{ fontWeight: 400 }}>
                  <strong>Recurring</strong> — when this {count}-week block finishes, automatically
                  start another {count}-week block for the same class (same roster, same day and
                  time), instead of needing a brand-new class.
                </span>
              </label>
            </div>
          )}

          {oneOffOnHoliday && firstStart && (
            <p className="error-text" style={{ marginTop: '-0.3rem' }}>
              {format(firstStart, 'MMM d')} is marked "
              {holidayLabel.get(dateKey(firstStart)) ?? 'No classes'}" — pick a different day.
            </p>
          )}

          {plan && firstStart && !oneOffOnHoliday && (
            <div className="date-preview" style={{ marginBottom: '0.9rem' }}>
              <strong>
                {plan.starts.length === 1
                  ? `1 session · ${format(plan.starts[0], 'EEE MMM d')} at ${formatTime(plan.starts[0].toISOString())}`
                  : `${plan.starts.length} sessions · ${format(plan.starts[0], 'MMM d')} – ${format(
                      plan.starts[plan.starts.length - 1],
                      'MMM d',
                    )} · ${formatDuration(durationMin)} each`}
              </strong>
              {plan.starts.length > 1 && (
                <ul>
                  {plan.starts.map((s) => (
                    <li key={s.toISOString()}>{format(s, 'EEE MMM d, yyyy')}</li>
                  ))}
                  {plan.skipped.map((s) => (
                    <li key={s.toISOString()} className="skipped">
                      {format(s, 'EEE MMM d')} — {holidayLabel.get(dateKey(s)) ?? 'no classes'}
                    </li>
                  ))}
                </ul>
              )}
              {plan.skipped.length > 0 && plan.starts.length > 1 && (
                <p className="muted small" style={{ margin: '0.4rem 0 0' }}>
                  No-class days are skipped automatically — the series runs a week longer instead.
                </p>
              )}
            </div>
          )}

          <div className="field">
            <label htmlFor="sc-notes">Class notes (optional)</label>
            <textarea
              id="sc-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Bring high-value treats and a 6-ft leash"
            />
          </div>

          {err && <p className="error-text">{err}</p>}
          <div className="row" style={{ justifyContent: 'flex-end', marginTop: '0.6rem' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={busy || !typeId || !instructorId || !firstStart || Boolean(oneOffOnHoliday)}
            >
              {busy ? 'Scheduling…' : 'Schedule class'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
